import { ActionCreator, AjaxDispatcher } from './ajax-dispatcher';
import { getApiUrl, getCurrentLanguageId } from './common';
import HttpClient from './http-client';
import { trackAddPaymentInfo, trackAddShippingInfo } from './analytics';

interface DeliveryOption {
  id: string;
  element: HTMLElement;
  radio: HTMLInputElement;
  type: 'shipping' | 'payment';
  relatedDeliveryOptions: DeliveryOption[];
}

class DeliveryOptionComponentState {
  shippingGuid: string;
  paymentGuid: string;
}

export default class DeliveryOptionComponent {
  private shippings: DeliveryOption[];
  private payments: DeliveryOption[];

  private selectedShipping: DeliveryOption;
  private selectedPayment: DeliveryOption;

  constructor() {
    const deliveryOptions = this.createDeliveryOptions();
    this.shippings = deliveryOptions.filter(x => x.type === 'shipping');
    this.payments = deliveryOptions.filter(x => x.type === 'payment');

    this.initState();
    this.addEventListeners();
    this.updateUI();
    this.reportInitialSelection();
  }

  private initState(): void {
    this.selectedShipping = this.shippings?.find(x => x.radio.checked);
    this.selectedPayment = this.payments?.find(x => x.radio.checked);
  }

  private async update(state: DeliveryOptionComponentState): Promise<void> {
    if (!this.stateChanged(state)) return;

    const previous = this.getCurrentState();
    const shippingChanged = !!state.shippingGuid && state.shippingGuid !== previous.shippingGuid;
    const paymentChanged = !!state.paymentGuid && state.paymentGuid !== previous.paymentGuid;

    // update internal state
    this.updateState(state);

    // update UI
    this.updateUI();

    // send to API
    await this.persist();

    if (shippingChanged) {
      this.reportShipping(this.selectedShipping, false);
    }
    if (paymentChanged) {
      this.reportPayment(this.selectedPayment, false);
    }

    this.reloadSummaryComponents();
  }

  private stateChanged(newState: DeliveryOptionComponentState): boolean {
    const currentState = this.getCurrentState();
    return (newState.shippingGuid && newState.shippingGuid !== currentState.shippingGuid)
      || (newState.paymentGuid && newState.paymentGuid !== currentState.paymentGuid);
  }

  private updateState(state: DeliveryOptionComponentState): void {

    // get the new target state
    const currentState = this.getCurrentState();
    const newState = {
      shippingGuid: state.shippingGuid ?? currentState.shippingGuid,
      paymentGuid: state.paymentGuid ?? currentState.paymentGuid
    } as DeliveryOptionComponentState;

    // update shipping
    const shipping = this.shippings.find(x => x.id === newState.shippingGuid);
    this.selectedShipping = shipping;
    if (!this.selectedShipping) {
      const availableShippings = this.shippings;
      if (availableShippings.length === 1) {
        this.selectedShipping = availableShippings[0];
      }
    }

    // update payment
    const payment = this.payments.find(x => x.id === newState.paymentGuid);
    this.selectedPayment = payment && this.isAvailableForShipping(payment, this.selectedShipping) ? payment : null;
    if (!this.selectedPayment) {
      const availablePayments = this.payments.filter(x => this.isAvailableForShipping(x, this.selectedShipping));
      if (availablePayments.length === 1) {
        this.selectedPayment = availablePayments[0];
      }
    }
  }

  private updateUI(): void {
    this.updateShippingUI();
    this.updatePaymentUI();
  }

  private updateShippingUI(): void {
    this.shippings.forEach(shipping => {
      shipping.radio.checked = shipping.id === this.selectedShipping?.id;
    });
  }

  private updatePaymentUI(): void {
    this.payments.forEach(payment => {
      (!this.selectedShipping) || this.isAvailableForShipping(payment, this.selectedShipping)
        ? this.enable(payment)
        : this.disable(payment);
      payment.radio.checked = payment.id === this.selectedPayment?.id;
    });
  }

  private async persist(): Promise<void> {
    const url = `${getApiUrl()}/cart/delivery-options`;
    const httpClient = new HttpClient();
    await httpClient.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Current-Language-Id': getCurrentLanguageId()
      },
      body: JSON.stringify(this.getCurrentState())
    });
  }

  private reportInitialSelection(): void {
    this.reportShipping(this.selectedShipping, true);
    this.reportPayment(this.selectedPayment, true);
  }

  private reportShipping(option: DeliveryOption, skipIfAlreadyReported: boolean): void {
    if (!option || !this.claimSelection('add_shipping_info', option.id, skipIfAlreadyReported)) {
      return;
    }
    const tier = option.element.getAttribute('data-ga-shipping-tier')
      || option.element.querySelector('.method-link__name')?.textContent?.trim()
      || option.id;
    trackAddShippingInfo(tier);
  }

  private reportPayment(option: DeliveryOption, skipIfAlreadyReported: boolean): void {
    if (!option || !this.claimSelection('add_payment_info', option.id, skipIfAlreadyReported)) {
      return;
    }
    const paymentType = option.element.getAttribute('data-ga-payment-type')
      || option.element.querySelector('.method-link__name')?.textContent?.trim()
      || option.id;
    trackAddPaymentInfo(paymentType);
  }

  /** Same cart (begin_checkout dedupe key) and the same option do not fire again after a reload. */
  private claimSelection(eventName: string, optionId: string, skipIfAlreadyReported: boolean): boolean {
    const key = `tronshop:analytics:${eventName}:${this.checkoutIdentity()}`;
    try {
      const previous = sessionStorage.getItem(key);
      if (skipIfAlreadyReported && previous === optionId) {
        return false;
      }
      sessionStorage.setItem(key, optionId);
    } catch {
    }
    return true;
  }

  private checkoutIdentity(): string {
    const script = document.querySelector('script[data-dedupe-key^="tronshop:analytics:begin_checkout"]');
    return script?.getAttribute('data-dedupe-key') || 'checkout';
  }

  private getCurrentState(): DeliveryOptionComponentState {
    return {
      shippingGuid: this.selectedShipping?.id,
      paymentGuid: this.selectedPayment?.id
    } as DeliveryOptionComponentState;
  }

  private isAvailableForShipping(payment: DeliveryOption, shipping: DeliveryOption): boolean {
    return payment && shipping && shipping && shipping.relatedDeliveryOptions.some(x => x.id === payment.id);
  }

  private addEventListeners(): void {
    this.shippings.forEach(shipping => {
      shipping.element.addEventListener('click', (event: Event) => {
        // don't fire event for child elements
        if (shipping.element !== event.target) {
          event.preventDefault();
        } this.update({ shippingGuid: shipping.element.dataset.id } as DeliveryOptionComponentState)
      })
    });

    this.payments.forEach(payment => {
      payment.element.addEventListener('click', (event: Event) => {
        // don't fire event for child elements
        if (payment.element !== event.target) {
          event.preventDefault();
        }
        this.isEnabled(payment) && this.update({ paymentGuid: payment.element.dataset.id } as DeliveryOptionComponentState)
      })
    });
  }

  private reloadSummaryComponents(): void {
    const action = new ActionCreator().reloadComponents(['CartSummary', 'CartHeaderIcon']);
    new AjaxDispatcher().dispatchGetAction(action);
  }

  private createDeliveryOptions(): DeliveryOption[] {
    const deliveryOptions: DeliveryOption[] = [...document.querySelectorAll('[data-delivery-type]')]
      .map(x => x as HTMLElement)
      .map(element => {
        return {
          id: element.dataset.id,
          element: element,
          radio: element.querySelector('input') as HTMLInputElement,
          type: element.dataset.deliveryType as 'shipping' | 'payment',
          relatedDeliveryOptions: []
        };
      });

    deliveryOptions.forEach(
      option =>
      (option.relatedDeliveryOptions = this.getRelatedDeliveryOptions(
        option.element.dataset.related,
        deliveryOptions
      ))
    );

    return deliveryOptions;
  }

  private disable(option: DeliveryOption) {
    option.element.classList.add('disabled');
    option.radio.setAttribute('disabled', 'disabled');
    option.radio.checked = false;
    option.radio.disabled = true;
  }

  private enable(option: DeliveryOption) {
    option.element.classList.remove('disabled');
    option.radio.removeAttribute('disabled');
    option.radio.disabled = false;
  }

  private isEnabled(option: DeliveryOption) {
    return !option.radio.disabled;
  }

  private getRelatedDeliveryOptions(dataRelatedString: string, deliveryOptions: DeliveryOption[]): DeliveryOption[] {
    if (!dataRelatedString) {
      return [];
    }

    const result = dataRelatedString
      .split(';')
      .filter(x => x !== '')
      .map(id => deliveryOptions.find(d => d.id === id))
      .filter(x => x !== undefined);

    return result;
  }

}


